/***
*
*   BUNDLE PACKAGING
*   Manage plans, features, add-ons, and settings
*
**********/

import { useState, useContext } from 'react';
import {
  Animate,
  Card,
  Button,
  Loader,
  ViewContext,
  // useNavigate,
} from 'components/lib';
import {
  useBundlePackaging,
  useFeatures,
  usePlanMutations,
  useFeatureMutations,
  useAddOnMutations,
  useSettingsMutations
} from 'components/hooks/bundlePackaging';
import { PlanCard } from 'components/bundlePackaging/PlanCard';
import { PlanEditor } from 'components/bundlePackaging/PlanEditor';
import { SettingsPanel } from 'components/bundlePackaging/SettingsPanel';
import { AddOnsPanel } from 'components/bundlePackaging/AddOnsPanel';
import { FeaturesPanel } from 'components/bundlePackaging/FeaturesPanel';
import Style from 'components/bundlePackaging/bundlePackaging.tailwind.js';

export function BundlePackaging(props) {
  const context = useContext(ViewContext);
  // const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState('plans');
  const [editingPlan, setEditingPlan] = useState(null);
  const [isNewPlan, setIsNewPlan] = useState(false);
  const [reload, setReload] = useState(0);

  // Hooks
  const bundleData = useBundlePackaging();
  const featuresData = useFeatures();
  const planMutations = usePlanMutations();
  const featureMutations = useFeatureMutations();
  const addOnMutations = useAddOnMutations();
  const settingsMutations = useSettingsMutations();

  const { data, loading, error } = bundleData;
  const { data: availableFeatures, loading: featuresLoading } = featuresData;

  if (loading || featuresLoading) return <Loader />;

  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-red-500">Error loading bundle packaging data</p>
          <p className="text-sm text-slate-400 mt-2">{error}</p>
        </div>
      </Card>
    );
  }

  const { plans = [], trial_settings, upgrade_downgrade, discounts, add_ons } = data || {};

  // Plan Handlers
  const handleCreatePlan = () => {
    setEditingPlan(null);
    setIsNewPlan(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setIsNewPlan(false);
  };

  const handleDuplicatePlan = (plan) => {
    setEditingPlan({
      ...plan,
      id: '',
      name: `${plan.name} (Copy)`,
    });
    setIsNewPlan(true);
  };

  const handleDeletePlan = (plan) => {
    context.modal.show({
      title: 'Delete Plan',
      form: {
        id: {
          type: 'hidden',
          value: plan.id,
        }
      },
      buttonText: 'Delete',
      text: `Are you sure you want to delete "${plan.name}"? This will also remove it from Stripe.`,
      url: `/api/dashboard/bundle-packaging/plans/${plan.id}`,
      method: 'DELETE',
      destructive: true,
    }, () => {
      context.notification.show('Plan deleted successfully', 'success', true);
      setReload(reload + 1);
    });
  };

  const handleSavePlan = async (planData) => {
    try {
      if (isNewPlan) {
        await planMutations.createPlan(planData);
        context.notification.show('Plan created successfully', 'success', true);
      } else {
        await planMutations.updatePlan(planData.id, planData);
        context.notification.show('Plan updated successfully', 'success', true);
      }
      setEditingPlan(null);
      setIsNewPlan(false);
      setReload(r => r + 1);
    } catch (err) {
      context.notification.show(err.message || 'Failed to save plan', 'error', true);
    }
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setIsNewPlan(false);
  };

  // Feature Handlers
  const handleSaveFeature = async (featureData, isNew) => {
    try {
      if (isNew) {
        await featureMutations.createFeature(featureData);
        context.notification.show('Feature created successfully', 'success', true);
      } else {
        await featureMutations.updateFeature(featureData.key, featureData);
        context.notification.show('Feature updated successfully', 'success', true);
      }
      setReload(r => r + 1);
    } catch (err) {
      context.notification.show(err.message || 'Failed to save feature', 'error', true);
    }
  };

  const handleDeleteFeature = (featureKey) => {
    context.modal.show({
      title: 'Delete Feature',
      form: {
        key: {
          type: 'hidden',
          value: featureKey,
        }
      },
      buttonText: 'Delete',
      text: 'Are you sure you want to delete this feature? Plans using this feature will need to be updated.',
      url: `/api/key/features/${featureKey}`,
      method: 'DELETE',
      destructive: true,
    }, () => {
      context.notification.show('Feature deleted successfully', 'success', true);
      setReload(reload + 1);
    });
  };

  // Add-on Handlers
  const handleCreateAddOn = async (addOnData) => {
    try {
      await addOnMutations.createAddOn(addOnData);
      context.notification.show('Add-on created successfully', 'success', true);
      setReload(r => r + 1);
    } catch (err) {
      context.notification.show(err.message || 'Failed to create add-on', 'error', true);
    }
  };

  const handleUpdateAddOn = async (addOnId, addOnData) => {
    try {
      await addOnMutations.updateAddOn(addOnId, addOnData);
      context.notification.show('Add-on updated successfully', 'success', true);
      setReload(r => r + 1);
    } catch (err) {
      context.notification.show(err.message || 'Failed to update add-on', 'error', true);
    }
  };

  const handleDeleteAddOn = (addOnId) => {
    context.modal.show({
      title: 'Delete Add-on',
      form: {
        id: {
          type: 'hidden',
          value: addOnId,
        }
      },
      buttonText: 'Delete',
      text: 'Are you sure you want to delete this add-on? Plans using this add-on will need to be updated.',
      url: `/api/dashboard/bundle-packaging/addons/${addOnId}`,
      method: 'DELETE',
      destructive: true,
    }, () => {
      context.notification.show('Add-on deleted successfully', 'success', true);
      setReload(reload + 1);
    });
  };

  // Settings Handlers
  const handleSaveTrialSettings = async (settings) => {
    try {
      await settingsMutations.updateTrialSettings(settings);
      context.notification.show('Trial settings saved', 'success', true);
      setReload(r => r + 1);
    } catch (err) {
      context.notification.show('Failed to save trial settings', 'error', true);
    }
  };

  const handleSaveUpgradeSettings = async (settings) => {
    try {
      await settingsMutations.updateUpgradeSettings(settings);
      context.notification.show('Upgrade settings saved', 'success', true);
      setReload(r => r + 1);
    } catch (err) {
      context.notification.show('Failed to save upgrade settings', 'error', true);
    }
  };

  const handleSaveDiscounts = async (discountSettings) => {
    try {
      await settingsMutations.updateDiscounts(discountSettings);
      context.notification.show('Discounts saved', 'success', true);
      setReload(r => r + 1);
    } catch (err) {
      context.notification.show('Failed to save discounts', 'error', true);
    }
  };

  const tabs = [
    { id: 'plans', label: 'Plans', count: plans.length },
    { id: 'features', label: 'Features', count: availableFeatures?.length || 0 },
    { id: 'addons', label: 'Add-ons', count: Object.keys(add_ons || {}).length },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <Animate type="pop">
      {/* Header */}
      <div className={Style.pageHeader}>
        <div>
          <h1 className={Style.pageTitle}>Bundle Packaging</h1>
          <p className={Style.pageSubtitle}>
            Manage plans, features, and pricing configuration
          </p>
        </div>
        {activeTab === 'plans' && (
          <Button
            text="Create Plan"
            icon="plus"
            color="green"
            action={handleCreatePlan}
          />
        )}
      </div>

      {/* Tabs */}
      <div className={Style.tabsContainer}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? Style.tabActive : Style.tab}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={Style.tabCount}>
                {tab.count}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'features' && (
        <FeaturesPanel
          features={availableFeatures}
          onSave={handleSaveFeature}
          onDelete={handleDeleteFeature}
          loading={featureMutations.loading}
        />
      )}

      {activeTab === 'plans' && (
        <div className={Style.plansGrid}>
          {plans.length > 0 ? (
            plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onEdit={handleEditPlan}
                onDelete={handleDeletePlan}
                onDuplicate={handleDuplicatePlan}
              />
            ))
          ) : (
            <Card>
              <div className={Style.emptyState}>
                <p className={Style.emptyStateText}>No plans configured yet</p>
                <Button
                  text="Create Your First Plan"
                  icon="plus"
                  color="green"
                  action={handleCreatePlan}
                />
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <SettingsPanel
          trialSettings={trial_settings}
          upgradeSettings={upgrade_downgrade}
          discounts={discounts}
          onSaveTrialSettings={handleSaveTrialSettings}
          onSaveUpgradeSettings={handleSaveUpgradeSettings}
          onSaveDiscounts={handleSaveDiscounts}
          loading={settingsMutations.loading}
        />
      )}

      {activeTab === 'addons' && (
        <AddOnsPanel
          addOns={add_ons}
          availableFeatures={availableFeatures}
          onCreate={handleCreateAddOn}
          onUpdate={handleUpdateAddOn}
          onDelete={handleDeleteAddOn}
          loading={addOnMutations.loading}
        />
      )}

      {/* Plan Editor Modal */}
      {(editingPlan || isNewPlan) && (
        <PlanEditor
          plan={editingPlan}
          isNew={isNewPlan}
          availableFeatures={availableFeatures}
          availableAddOns={add_ons}
          onSave={handleSavePlan}
          onCancel={handleCancelEdit}
          loading={planMutations.loading}
        />
      )}
    </Animate>
  );
}